import Message from 'Components/Message';
import Modal from 'Components/Modal/Modal';
import Spinner from 'Components/Spinner/Spinner';
import { H2, Input, Label, LpBox, LpDetails, P, Row } from 'Constants/styles';
import { ChangeEvent, FormEvent, MouseEvent, useEffect, useState } from 'react';
import getFetchOptions from 'utils/getFetchOptions';
import handleApiError from 'utils/handleApiError';
import setBodyOverflow from 'utils/setBodyOverflow';
import useDebounce from 'utils/useDebounce';
import validateEmail from 'utils/validateEmail';

import * as s from './OrderNotifications.styles';
import { Cno, EditBtn, LpRadio, RadioLi, RadioUl } from './styles';
import { OrderNotificationsAPIResponse } from './types';

type OrderNotificationsType = {
  apiBaseUri: string;
  merchantId: string;
};

enum Notifications {
  Enabled = 'enabled',
  Disabled = 'disabled',
}

const OrderNotifications = ({ apiBaseUri, merchantId }: OrderNotificationsType) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [orderNotifications, setOrderNotifications] = useState<OrderNotificationsAPIResponse | null>(null);
  const [now, setNow] = useState<number>(0);

  // modal states
  const [modalLoading, setModalLoading] = useState<boolean>(false);
  const [modalErrorMsg, setModalErrorMsg] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [emails, setEmails] = useState<string>('');
  const [notifications, setNotifications] = useState<Notifications>(Notifications.Disabled);
  const [validEmail, setValidEmail] = useState<boolean>(true);
  const [inputHasFocus, setInputHasFocus] = useState<boolean>(false);

  const debouncedEmails = useDebounce(emails, 500);

  useEffect(() => {
    if (emails !== debouncedEmails) {
      return;
    }
    const isValid = emails.split(',').every((e) => validateEmail(e.trim()));
    setValidEmail(isValid);
  }, [emails, debouncedEmails]);

  useEffect(() => {
    if (!apiBaseUri || !merchantId) {
      return;
    }

    let isMounted = true;
    const fetchPayouts = async () => {
      try {
        setLoading(true);
        const url = `${apiBaseUri}/merchants/${merchantId}/settings/order-notifications`;
        const options = await getFetchOptions();
        const res = await fetch(url, options);
        if (!res.ok) {
          await handleApiError(res);
        }
        if (isMounted) {
          setOrderNotifications(await res.json());
          setErrorMsg('');
        }
      } catch (e) {
        isMounted && setErrorMsg(e.message || 'Failed to fetch order notifications');
      } finally {
        isMounted && setLoading(false);
      }
    };

    fetchPayouts();

    return () => {
      isMounted = false;
    };
  }, [apiBaseUri, merchantId, now]);

  const handleCloseErrorMsg = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setErrorMsg('');
  };

  const handleCloseModalErrorMsg = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setModalErrorMsg('');
  };

  const handleShowModal = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setShowModal(true);
    setModalLoading(false);
    setModalErrorMsg('');
    setBodyOverflow('hidden');
    setEmails(orderNotifications?.orderPaidNotificationEmails?.join(', ') || '');
    setValidEmail(true);
    const _notifications = orderNotifications?.orderPaidNotificationsEnabled || false;
    setNotifications(!!_notifications ? Notifications.Enabled : Notifications.Disabled);
    setInputHasFocus(false);
  };

  const handleCancelModal = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setModalLoading(false);
    setModalErrorMsg('');
    setBodyOverflow('auto');
    setShowModal(false);
    setValidEmail(true);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      setModalLoading(true);
      const url = `${apiBaseUri}/merchants/${merchantId}/settings/order-notifications`;
      const body = {
        orderPaidNotificationEmails: emails.split(',').map((e) => e.trim()),
        orderPaidNotificationsEnabled: notifications === Notifications.Enabled ? true : false,
      };
      const options = await getFetchOptions('POST', JSON.stringify(body));
      const res = await fetch(url, options);
      if (!res.ok) {
        await handleApiError(res);
      }
      setModalErrorMsg('');
      setBodyOverflow('auto');
      setShowModal(false);
      setNow(Date.now());
    } catch (e) {
      setModalErrorMsg(e.message || 'Failed to save payment confirmation settings');
    } finally {
      setModalLoading(false);
    }
  };

  const onChangeNotification = (e: ChangeEvent<HTMLInputElement>): void => {
    setNotifications(e.target.value as Notifications);
  };

  const handleChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setEmails(value);
  };

  const handleFocusEmail = () => {
    setInputHasFocus(true);
  };

  const handleBlurEmail = () => {
    setInputHasFocus(false);
  };

  return (
    <>
      {!!errorMsg && <Message description={errorMsg} success={false} handleClose={handleCloseErrorMsg} />}
      <LpBox>
        <Row>
          <div className="col-sm-6">
            <H2 data-testid="payment-confirmations-header">Payment confirmations</H2>
          </div>
          <div className="col-sm-6 text-left text-sm-right">
            {!!orderNotifications && (
              <EditBtn data-testid="edit-payment-confirmation-btn" onClick={handleShowModal}>
                Edit payment confirmation settings
              </EditBtn>
            )}
          </div>
        </Row>
        <LpDetails>
          <Row>
            <div className="col-sm-12">
              <P data-testid="payment-confirmation-description" className="mt-2 mb-0 mb-md-3">
                Receive an email notification after new transactions are processed.
              </P>
            </div>
          </Row>
          {loading && (
            <s.SpinnerWrapper>
              <Spinner />
            </s.SpinnerWrapper>
          )}
          {!!orderNotifications && !loading && (
            <>
              <Row className="pt-4">
                <div className="col-sm-4 col-xl-3">
                  <Label data-testid="payment-confirmation-notifications-label">Notifications</Label>
                </div>
                <div className="col-sm-8 col-xl-9" data-testid="payment-confirmation-notifications-value">
                  {orderNotifications.orderPaidNotificationsEnabled ? 'Enabled' : 'Disabled'}
                </div>
              </Row>
              {orderNotifications.orderPaidNotificationsEnabled && (
                <Row className="pt-4">
                  <div className="col-sm-4 col-xl-3">
                    <Label data-testid="payment-confirmation-recipients-label">Recipients</Label>
                  </div>
                  <div data-testid="payment-confirmation-recipients-value" className="col-sm-8 col-xl-9">
                    {orderNotifications.orderPaidNotificationEmails?.join(', ')}
                  </div>
                </Row>
              )}
            </>
          )}
        </LpDetails>
        {showModal && (
          <Modal
            title="Edit payment confirmation settings"
            cancelBtnText="Cancel"
            confirmBtnText="Save"
            handleCancel={handleCancelModal}
            handleSubmit={handleSubmit}
            isLoading={modalLoading}
            disableConfirmBtn={
              modalLoading || !validEmail || (notifications === Notifications.Enabled && emails.length === 0)
            }
          >
            {!!modalErrorMsg && (
              <Message
                className="mt-4"
                description={modalErrorMsg}
                success={false}
                handleClose={handleCloseModalErrorMsg}
              />
            )}
            <P data-testid="notifications-modal-description">
              Receive an email notification after new transactions are processed.
            </P>
            <RadioUl>
              <RadioLi>
                <LpRadio>
                  <Cno data-testid="disable-notifications-label">Disable notifications</Cno>
                  <input
                    type="radio"
                    name="NotificationRadio"
                    value={Notifications.Disabled}
                    onChange={onChangeNotification}
                    checked={notifications === Notifications.Disabled}
                    data-testid="disable-notifications-input"
                  />
                  <span className="check-mark" />
                </LpRadio>
              </RadioLi>
              <RadioLi>
                <LpRadio>
                  <Cno data-testid="enable-notifications-label">Enable notifications</Cno>
                  <input
                    type="radio"
                    name="NotificationRadio"
                    value={Notifications.Enabled}
                    onChange={onChangeNotification}
                    checked={notifications === Notifications.Enabled}
                    data-testid="enable-notifications-input"
                  />
                  <span className="check-mark" />
                </LpRadio>
                {notifications === Notifications.Enabled && (
                  <Row className="mt-3">
                    <div className="col-12">
                      <Label>Recipients</Label>
                      <Input
                        autoComplete="off"
                        placeholder="name@domain.com, nametwo@domain.com"
                        value={emails}
                        onChange={handleChangeEmail}
                        onFocus={handleFocusEmail}
                        onBlur={handleBlurEmail}
                        data-testid="notifications-emails-input"
                      />
                      {validEmail && inputHasFocus && (
                        <s.Small data-testid="notifications-emails-valid-text" isValid>
                          Multiple email addresses can be added, separated by a comma.
                        </s.Small>
                      )}
                      {!validEmail && (
                        <s.Small data-testid="notifications-emails-invalid-text" isValid={false}>
                          Please enter a valid email
                        </s.Small>
                      )}
                    </div>
                  </Row>
                )}
              </RadioLi>
            </RadioUl>
          </Modal>
        )}
      </LpBox>
    </>
  );
};

export default OrderNotifications;
